import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../redux/store';
import { upsertShortstay, listenToShortstayData } from '../../../../redux/shortstaysSlice';
import { Box, TextField, Button, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

const ShortForm4: React.FC = () => {
  const { userId, listingId } = useParams<{ userId: string; listingId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const currentListing = useSelector((state: RootState) => state.shortstays.currentListing);

  const [form, setForm] = useState({
    guests: '1',
    bedrooms: '0',
    beds: '0',
    bathrooms: '0',
    toilets: '0',
    squareMeters: '0',
    parkingLots: '0',
    floorNumber: '0',
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (listingId) {
      dispatch(listenToShortstayData(listingId));
    }
  }, [listingId, dispatch]);

  useEffect(() => {
    if (currentListing) {
      setForm((prev) => ({
        ...prev,
        ...currentListing,
        guests: currentListing.guestCount?.toString() || prev.guests,
      }));
    }
  }, [currentListing]);

  const handleChange = <T extends keyof typeof form>(key: T, value: string) => {
    const number = Math.max(0, parseInt(value) || 0);
    setForm((prev) => ({ ...prev, [key]: number.toString() }));
  };

  const handleStep = <T extends keyof typeof form>(key: T, diff: number) => {
    const current = parseInt(form[key] || '0') || 0;
    const stepSize = key === 'squareMeters' ? 30 : 1;
    const next = Math.max(0, current + diff * stepSize);
    setForm((prev) => ({ ...prev, [key]: next.toString() }));
  };

  const handleNext = async () => {
    if (!listingId) return;
    setLoading(true);

    // ✅ Rename guests → guestCount (as number)
    const payload = {
      ...form,
      guestCount: Number(form.guests),
    };
    delete (payload as any).guests;

    await dispatch(upsertShortstay({ listingId, data: payload }));
    setSuccess(true);
    setTimeout(() => {
      setLoading(false);
      navigate(`/shortform5/${userId}/${listingId}`);
    }, 1000);
  };

  const handleBack = () => {
    navigate(`/shortform3/${userId}/${listingId}`);
  };

  const fields = [
    { key: 'guests', label: 'Guests' },
    { key: 'bedrooms', label: 'Bedrooms' },
    { key: 'beds', label: 'Beds' },
    { key: 'bathrooms', label: 'Bathrooms' },
    { key: 'toilets', label: 'Toilets (optional)' },
    { key: 'squareMeters', label: 'Square Meters (increments of 30)' },
    { key: 'parkingLots', label: 'Parking Lots' },
    { key: 'floorNumber', label: 'Floor Number' },
  ] as const;

  return (
    <Box
      sx={{
        backgroundColor: '#000',
        color: '#fff',
        minHeight: '100vh',
        pb: 12,
        position: 'relative',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={60} sx={{ color: '#bf9b30' }} />
        </Box>
      )}
      {success && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            backdropFilter: 'blur(3px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 80, color: '#bf9b30' }} />
        </Box>
      )}

      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <h2 style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 10, color: '#bf9b30' }}>
          Share some basics about your place
        </h2>
        <p style={{ fontSize: 14, color: '#ccc', marginBottom: 20 }}>
          You'll add more details later, like bed types.
        </p>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          {fields.map(({ key, label }) => (
            <Box key={key}>
              <label
                style={{
                  fontSize: 13,
                  marginBottom: 4,
                  display: 'block',
                  color: '#bf9b30',
                }}
              >
                {label}
              </label>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  onClick={() => handleStep(key, -1)}
                  variant="outlined"
                  sx={{
                    minWidth: 40,
                    borderColor: '#bf9b30',
                    color: '#bf9b30',
                    '&:hover': {
                      borderColor: '#fff',
                      color: '#fff',
                    },
                  }}
                >
                  <RemoveIcon />
                </Button>
                <TextField
                  type="number"
                  value={form[key] || '0'}
                  onChange={(e) => handleChange(key, e.target.value)}
                  fullWidth
                  inputProps={{ min: 0 }}
                  sx={textInput}
                />
                <Button
                  onClick={() => handleStep(key, 1)}
                  variant="outlined"
                  sx={{
                    minWidth: 40,
                    borderColor: '#bf9b30',
                    color: '#bf9b30',
                    '&:hover': {
                      borderColor: '#fff',
                      color: '#fff',
                    },
                  }}
                >
                  <AddIcon />
                </Button>
              </Box>
            </Box>
          ))}
        </Box>

        <p
          style={{
            color: '#aaa',
            fontSize: 13,
            textAlign: 'center',
            marginTop: 30,
          }}
        >
          Your progress: step 5 out of 17
        </p>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#000',
          borderTop: '1px solid #444',
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          zIndex: 10,
        }}
      >
        <Button
          onClick={handleBack}
          variant="outlined"
          sx={{
            borderRadius: 3,
            color: '#fff',
            borderColor: '#777',
            '&:hover': { borderColor: '#bf9b30', color: '#bf9b30' },
          }}
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          sx={{
            borderRadius: 3,
            background: 'linear-gradient(to right, #fff, #bf9b30)',
            color: '#000',
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(to right, #bf9b30, #fff)',
            },
          }}
        >
          NEXT
        </Button>
      </Box>
    </Box>
  );
};

export default ShortForm4;

const textInput = {
  backgroundColor: '#fff',
  borderRadius: 2,
  input: { color: '#000' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ccc' },
    '&:hover fieldset': { borderColor: '#bf9b30' },
    '&.Mui-focused fieldset': { borderColor: '#bf9b30' },
  },
};
